import { HelperService } from './../core/services/helper.service';
import { Router } from '@angular/router';
import { PortalContext } from './models/portal-context';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NotificationService } from '@optsolservices/ngx-notification';
import { BaseService } from '../core/services/base.service';
import { SharedService } from '../shared/shared.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { DownloadDbResourceResponse, PageAction } from './models/page-config';
import { DynamicFormDialogComponent } from './dynamic-form-dialog/dynamic-form-dialog.component';
import * as fileSaver from 'file-saver';
import { HttpClient } from '@angular/common/http';
import { v4 as uuidv4 } from 'uuid';
import { ConfirmDialogComponent } from '@optsolservices/ngx-confirm-dialog';
import { DataImportDialogComponent } from './data-import-dialog/data-import-dialog.component';
import { DataImportDialogComponent2 } from './data-import-dialog-2/data-import-dialog-2.component';
import { GenericScannerDialogComponent } from './generic-scanner-dialog/generic-scanner-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class GenericService {

  constructor(
    private sharedService: SharedService,
    private notificationService: NotificationService,
    private baseService: BaseService,
    private dialog: MatDialog,
    private clipboard: Clipboard,
    private router: Router,
    private helperService: HelperService,
    private http: HttpClient
  ) { }

  getUniquePortalContext(): PortalContext {
    return {
      pageIdentifier: uuidv4()
    };
  };

  refreshAfterAction(action: PageAction, event: any) {
    if (action.refreshMasterTable === true && event.dataTableWithSearchCriteria) { event.dataTableWithSearchCriteria.refresh(); }
    if (action.refreshMasterTable === true && event.dataTableMasterDetail && event.dataTableMasterDetail.dataTableWithSearchCriteriaComponent) { event.dataTableMasterDetail.dataTableWithSearchCriteriaComponent.refresh(); }
    if (action.refreshTabTable === true && event.dataTableTabGroup) { event.dataTableTabGroup.refresh(); }
  }

  actionButtonClicked(pageCode: string, pageType: string, event: any, pageActions: PageAction[]) {
    switch (pageType) {
      case 'data-table-tab-group':
        this.performActionDataTableTabGroup(event, pageActions);
        break;

      case 'detail-section-tab-group':
        this.performActionDetailSectionTabGroup(event, pageActions);
        break;

      default:
        this.notificationService.error(`Actions for page type ${pageType} not supported yet`);
        break;
    }

  }

  performActionDataTableTabGroup(event: any, pageActions: PageAction[]) {
    if (!pageActions) {
      this.notificationService.error(`No page actions found for this page.`);
      return;
    }

    let action: PageAction;

    // if we are in a table of a tab, we need to have a tabCode
    if (event.dataTableTabGroup && !event.tabCode) {
      this.notificationService.error(`No tabCode defined for action ${event.actionCode} on tab ${event.dataTableTabGroup.tabConfig[event.dataTableTabGroup.selectedTabIndex].tabName}`);
      return;
    }

    if (!event.tabCode) {
      action = pageActions.find(f => (f.tabCode === undefined || f.tabCode === null) && f.actionCode === event.actionCode);
    } else {
      action = pageActions.find(f => f.tabCode === event.tabCode && f.actionCode === event.actionCode);
    }

    if (!action) {
      if (!event.tabCode) {
        this.notificationService.error(`No action found for action code ${event.actionCode}`);
      } else {
        this.notificationService.error(`No action found for action code ${event.actionCode} on tab ${event.tabCode}`);
      }
      return;
    }

    let additionalData = null;
    if (action.tabCode && event.dataTableTabGroup && event.dataTableTabGroup.dataBody) {
      additionalData = event.dataTableTabGroup.dataBody;
    } else {
      // set the additionalData to the event
      additionalData = event;
    }

    this.performAction(action, event, additionalData);

  }

  performActionDetailSectionTabGroup(event: any, pageActions: PageAction[]) {
    if (!pageActions) {
      this.notificationService.error(`No page actions found for this page.`);
      return;
    }

    let action: PageAction;

    if (!event.tabCode) {
      action = pageActions.find(f => f.sectionCode === event.sectionCode && f.actionCode === event.actionCode);
    } else {
      action = pageActions.find(f => f.tabCode === event.tabCode && f.actionCode === event.actionCode);
    }

    if (!action) {
      if (!event.tabCode) {
        this.notificationService.error(`No action found for action code ${event.actionCode} on section ${event.sectionCode}`);
      } else {
        this.notificationService.error(`No action found for action code ${event.actionCode} on tab ${event.tabCode}`);
      }
      return;
    }

    this.performAction(action, event);

  }

  performAction(action: PageAction, event: any, additionalData?: any) {
    switch (action.actionType) {
      case "API-FUNCTION":
        this.executeApiFunction(action, event, additionalData);
        break;

      case "FORM-DIALOG":
        this.executeFormDialog(action, event, additionalData);
        break;

      case "DYNAMIC-DIALOG":
        this.executeDynamicDialog(action, event, additionalData);
        break;

      case "DATA-IMPORT":
        // Figure out if we're using the old or new DATA-IMPORT
        // Old
        if (!action.actionData.dataImportConfig) {
          this.executeDataImportDialog(action, event, additionalData);
        }
        // New
        else {
          this.executeDataImportDialog2(action, event, additionalData);
        }
        break;

      case "CLIPBOARD":
        this.executeClipboard(action, event, additionalData);
        break;

      case "ROUTER-LINK":
        this.goToRouterLink(action, event, additionalData);
        break;

      case "DOWNLOAD-RESOURCE":
        this.downloadResource(action, event, additionalData);
        break;

      case "GENERATE-REPORT":
        this.generateReport(action, event, additionalData);
        break;

      case "DOWNLOAD-DB-RESOURCE":
        this.downloadDbResource(action, event, additionalData);
        break;

      case "SCANNER-DIALOG":
        this.executeScannerDialog(action, event, additionalData);
        break;

      default:
        this.notificationService.error(`Action type ${action.actionType} not supported yet`);
        break;
    }
  }

  executeApiFunction(action: PageAction, event: any, additionalData?: any) {
    if (!action.actionData || !action.actionData.apiFunction) {
      this.notificationService.error(`Api function not found in actionData.`);
      return;
    }


    if (action.actionData.confirmDialog) {
      const instance = new ConfirmDialogComponent(this.dialog);
      instance.title = this.helperService.performStringReplacements(action.actionData.confirmDialog.title, event.data, action.actionData.confirmDialog.replacementToken);
      instance.text = this.helperService.performStringReplacements(action.actionData.confirmDialog.text, event.data, action.actionData.confirmDialog.replacementToken);
      instance.showCancelButton = action.actionData.confirmDialog.showCancelButton;
      instance.showConfirmButton = action.actionData.confirmDialog.showConfirmButton;
      instance.cancelButtonText = action.actionData.confirmDialog.cancelButtonText;
      instance.confirmButtonText = action.actionData.confirmDialog.confirmButtonText;
      instance.open = 1;
      instance.load();
      instance.actionPerformed.subscribe(confirmDialogAction => {
        if (confirmDialogAction.confirmButtonClicked === true) {
          this.baseService.executeCommand(action.actionData.apiFunction, event.data ?? event.actionData)
            .subscribe(response => {
              let responseData = (response.data as any);
              if (responseData?.action) {
                this.performAction(responseData.action, event, responseData.additionalData);
              } else {
                this.refreshAfterAction(action, event);
              }
            });
        }
      });
    } else {
      this.baseService.executeCommand(action.actionData.apiFunction, event.data ?? event.actionData)
        .subscribe(response => {
          let responseData = (response.data as any);
          if (responseData?.action) {
            this.performAction(responseData.action, event, responseData.additionalData);
          } else {
            this.refreshAfterAction(action, event);
          }
        });
    }
  }

  executeFormDialog(action: PageAction, event: any, additionalData?: any) {
    const dialogRef = this.dialog.open(DynamicFormDialogComponent, {
      data: {
        formFields: action.actionData?.formFields,
        initialFormValue: action.actionData.data ?? event.actionData ?? event?.data ?? additionalData,
        dialogTitle: action.actionData?.dialogTitle,
        dialogText: action.actionData?.dialogText,
        action: action,
        fxLayout: action.actionData?.fxLayout,
        fxLayoutAlign: action.actionData?.fxLayoutAlign,
        fxLayoutGap: action.actionData?.fxLayoutGap,

        additionalData: additionalData,

        buttons: action.actionData?.buttons,
      },
      width: action.actionData?.dialogWidth,
      height: action.actionData?.dialogHeight,
    })

    dialogRef.afterClosed()
      .subscribe((responseData: { data: any; action: PageAction, additionalData: any }) => {
        if (responseData) {
          if (responseData.action) {
            this.performAction(responseData.action, event, responseData.additionalData);
          } else {
            this.refreshAfterAction(action, event);
          }
        } else {
          this.refreshAfterAction(action, event);
        }
      });
  }

  executeFormDialogPromise<T>(configProc: string, configData: any): Promise<T> {
    return new Promise<T>(async (resolve, reject) => {
      try {
        const dialogConfig = await this.baseService.executeCommand<any>(configProc, configData).toPromise();
        if (dialogConfig.responseCode === 0) {
          const dialogRef = this.dialog.open(DynamicFormDialogComponent, {
            data: {
              formFields: dialogConfig.data.form.formFields,
              dialogTitle: dialogConfig.data.form.dialogTitle,
              dialogText: dialogConfig.data.form.dialogText,
              fxLayout: dialogConfig.data.form.fxLayout,
              fxLayoutAlign: dialogConfig.data.form.fxLayoutAlign,
              fxLayoutGap: dialogConfig.data.form.fxLayoutGap,
              action: dialogConfig.data.action,
              initialFormValue: dialogConfig.data.initialFormValue,
              additionalData: dialogConfig.data.additionalData,
              buttons: dialogConfig.data.form.buttons,
            },
            width: dialogConfig.data.form.dialogWidth,
            height: dialogConfig.data.form.dialogHeight,
          });

          dialogRef.afterClosed()
            .subscribe(formValue => {
              resolve(formValue);
            });

        } else {
          reject('Failed to retrieve config');
        }

      } catch (error) {
        reject(error);
      }
    });
  }

  executeDynamicDialog(action: PageAction, event: any, additionalData?: any) {
    if (!action.actionData || !action.actionData.dialog) {
      this.notificationService.error(`No dialog name found on action data`);
      return;
    }
    const customDialog = this.sharedService.customDialogMapper[action.actionData.dialog];
    if (!customDialog) {
      this.notificationService.error(`No dialog mapping found ${action.actionData.dialog}`);
      return;
    }

    const dialogRef = this.dialog.open(customDialog, {
      data: { ...event.data, ...event.actionData, additionalData, ...action.actionData },
      width: action.actionData?.dialogWidth,
      height: action.actionData?.dialogHeight,
    })

    dialogRef.afterClosed()
      .subscribe(responseData => {
        if (responseData) {
          if (responseData.action) {
            this.performAction(responseData.action, event, responseData.additionalData);
          } else {
            this.refreshAfterAction(action, event);
          }
        }
      });
  }

  // Old
  executeDataImportDialog(action: PageAction, event: any, additionalData?: any) {
    const dialogRef = this.dialog.open(DataImportDialogComponent, {
      data: {
        dialogTitle: action.actionData?.dialogTitle,

        configProc: action.actionData?.configProc,
        contentProc: action.actionData?.contentProc,
        actionProc: action.actionData?.actionProc,

        action: action,
        additionalData: additionalData
      },
      width: action.actionData?.dialogWidth,
      height: action.actionData?.dialogHeight,
    })

    dialogRef.afterClosed()
      .subscribe(formValue => {
        if (formValue) {
          this.refreshAfterAction(action, event);
        }
      });
  }

  // New
  executeDataImportDialog2(action: PageAction, event: any, additionalData?: any) {
    this.baseService.executeCommand(action.actionData.dataImportConfig, {})
      .subscribe(response => {
        if (response.responseCode === 0) {
          const dialogRef = this.dialog.open(DataImportDialogComponent2, {
            data: {
              config: response.data
            },
            width: action.actionData?.dialogWidth,
            height: action.actionData?.dialogHeight,
          })

          dialogRef.afterClosed()
            .subscribe(result => {
              if (result) {
                this.refreshAfterAction(action, event);
              }
            });
        }
      });

  }

  executeClipboard(action: PageAction, event: any, additionalData?: any) {
    if (!action.actionData || !action.actionData.field) {
      this.notificationService.error(`Field to copy not found in actionData.`);
      return;
    }
    this.clipboard.copy(event?.data[action.actionData.field]);
    this.notificationService.success(`Copied ${action.actionData.field} to clipboard!`);
  }

  goToRouterLink(action, event, additionalData) {
    if (action.actionData.route.includes(':id')) {
      if (action.actionData.keyField === null || action.actionData.keyField === undefined) {
        this.notificationService.error(`The keyField was not provided in the actionData.`);
      } else {
        if (event.data) {
          action.actionData.route = action.actionData.route.replace(':id', event.data[action.actionData.keyField]);
        } else if (event.dataTableTabGroup?.dataBody && event.dataTableTabGroup?.dataBody[action.actionData.keyField]) {
          action.actionData.route = action.actionData.route.replace(':id', event.dataTableTabGroup?.dataBody[action.actionData.keyField]);
        } else {
          this.notificationService.error(`Route to navigate to not found in event.data or event.dataTableTabGroup.dataBody.`);
          return;
        }
      }
    }
    if (!action.actionData || !action.actionData.route) {
      this.notificationService.error(`Route to navigate to not found in the actionData.`);
      return;
    }
    this.router.navigate([action.actionData.route]);
  }

  downloadResource(action, event, additionalData) {
    if ((!event.data || !event.data.apiResourceKey) && (!action || !action.actionData || !action.actionData.apiResourceKey)) {
      this.notificationService.error(`apiResourceKey to download not found in the row data.`);
      return;
    }

    if ((!event.data || !event.data.fileName) && (!action || !action.actionData || !action.actionData.fileName)) {
      this.notificationService.error(`fileName not found in the row data.`);
      return;
    }

    if ((!event.data || !event.data.mimeType) && (!action || !action.actionData || !action.actionData.mimeType)) {
      this.notificationService.error(`mimeType not found in the row data.`);
      return;
    }

    if (event.data && event.data.apiResourceKey && event.data.fileName && event.data.mimeType) {
      this.baseService.downloadResource(event.data.apiResourceKey, event.data.fileName, event.data.mimeType);
    } else {
      this.baseService.downloadResource(action.actionData.apiResourceKey, action.actionData.fileName, action.actionData.mimeType);
    }
  }

  downloadDbResource(action, event, additionalData) {
    /*
        If we clicked a table header button, data is in action.actionData.data
        If we clicked a table row button, data is in event.data
    */
    const body = event.data || action.actionData?.data;
    if (!body) {
      this.notificationService.error(`no data found to pass through to the generic api call.`);
      return;
    }

    this.baseService.executeCommand(action.actionData.apiFunction, body)
      .subscribe(response => {
        if (response.responseCode === 0) {

          const responseData = (response.data as DownloadDbResourceResponse);
          if (!responseData) {
            this.notificationService.error(`data not found on the DB response.`);
            return;
          }

          if (!responseData.base64Data) {
            this.notificationService.error(`base64Data not found on the DB response data property.`);
            return;
          }

          if (!responseData.fileExtension) {
            this.notificationService.error(`fileExtension not found on the DB response data property.`);
            return;
          }

          if (!responseData.filename) {
            this.notificationService.error(`filename not found on the DB response data property.`);
            return;
          }

          const blob = this.helperService.b64toBlob(responseData.base64Data);
          const blobUrl = URL.createObjectURL(blob);

          const a: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;
          a.href = blobUrl;
          a.download = `${responseData.filename}.${responseData.fileExtension}`;
          document.body.appendChild(a);
          a.click();

          document.body.removeChild(a);
          URL.revokeObjectURL(blobUrl);
        }
      });
  }

  generateReport(action, event, additionalData) {
    /*{
    reportParameters: {

    },
    report: {
      md5has: "",
      mimeType: "",
      filename: ""
    }
}
*/
const generateReportConfig = action?.data?.generateReportConfig || event?.data?.generateReportConfig;
    if (generateReportConfig === null || generateReportConfig === undefined) {
      this.notificationService.error(`The generateReportConfig was not provided in the row.`);
    } else {

      //TODO: validate
      /*
            var config = data["reportConfig"];
            var fileExtension = config["fileExtension"].ToString();
            var mimeType = config["mimeType"].ToString();

            var reportParams = data["reportParameters"];
      */


      this.baseService.generateReport(generateReportConfig);
    }


  }

  executeScannerDialog(action: PageAction, event: any, additionalData?: any) {
    let scannerDialogConfig = null;

    if (action.actionData?.formConfig?.formFields) {
      scannerDialogConfig = {
        data: {
          formConfig: {
            formFields: action.actionData?.formConfig?.formFields,
            initialFormValue: action.actionData?.formConfig?.data ?? event.actionData ?? event?.data ?? additionalData,
            action: action,
            fxLayout: action.actionData?.formConfig?.fxLayout,
            fxLayoutAlign: action.actionData?.formConfig?.fxLayoutAlign,
            fxLayoutGap: action.actionData?.formConfig?.fxLayoutGap,
            additionalData: additionalData,
            buttons: action.actionData?.buttons,
          },

          scannerConfig: action.actionData?.scannerConfig,

          dialogConfig: action.actionData?.dialogConfig,
        },
        width: action.actionData?.dialogConfig?.dialogWidth,
        height: action.actionData?.dialogConfig?.dialogHeight,
      };

    } else {
      scannerDialogConfig = {
        data: {
          scannerConfig: action.actionData?.scannerConfig,

          dialogConfig: action.actionData?.dialogConfig,
        },
        width: action.actionData?.dialogConfig?.dialogWidth,
        height: action.actionData?.dialogConfig?.dialogHeight,
      };
    }

    const dialogRef = this.dialog.open(GenericScannerDialogComponent, scannerDialogConfig);

    dialogRef.afterClosed()
      .subscribe((responseData: { data: any; action: PageAction, additionalData: any }) => {
        if (responseData) {
          if (responseData.action) {
            this.performAction(responseData.action, event, responseData.additionalData);
          } else {
            this.refreshAfterAction(action, event);
          }
        } else {
          this.refreshAfterAction(action, event);
        }
      });
  }
}
